import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const BLOCKED_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form', 'input', 'button'];

/**
 * Removes scripts, event handlers and javascript: URLs from HTML coming from the API,
 * keeping formatting tags such as <b>, <br>, <p> and media like <video>.
 */
export function sanitizeHtml(html: string) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.body.querySelectorAll(BLOCKED_TAGS.join(',')).forEach(element => element.remove());
    doc.body.querySelectorAll('*').forEach(element => {
        Array.from(element.attributes).forEach(attribute => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value.trim().toLowerCase();
            if (name.startsWith('on') || ((name === 'href' || name === 'src') && value.startsWith('javascript:'))) {
                element.removeAttribute(attribute.name);
            }
        });
    });
    return doc.body.innerHTML;
}

const hasHtml = (text: string) => /<[a-z][\s\S]*>/i.test(text);

interface IHtmlTextProps {
    text: string | null | undefined;
    className?: string;
}

/** Renders text from the API, applying its HTML formatting when it contains tags. */
export const HtmlText: React.FC<IHtmlTextProps> = ({ text, className }) => {
    if (!text) {
        return null;
    }
    return hasHtml(text) ?
        <span className={className} dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} /> :
        <span className={className}>{text}</span>;
};

/** Round help icon that shows the (HTML) help text on hover. */
export const HelpIcon: React.FC<{ helpText: string | null | undefined }> = ({ helpText }) => {
    if (!helpText) {
        return null;
    }
    return (
        <Tooltip arrow placement='top' enterDelay={100}
            title={<HtmlText text={helpText} className='f433-help-tooltip' />}
            slotProps={{ tooltip: { sx: { maxWidth: 420, fontSize: 12, lineHeight: 1.5 } } }}>
            <HelpOutlineIcon className='f433-help-icon' aria-label='Help' tabIndex={0} />
        </Tooltip>
    );
};
